import type { SearchSuggestion } from './types'

const toSafeText = (value: unknown) => (typeof value === 'string' ? value.trim() : '')
const PUBLIC_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{8}$/

const normalizePublicCode = (value: unknown): string | undefined => {
  const normalized = toSafeText(value).toUpperCase()
  if (!PUBLIC_CODE_PATTERN.test(normalized)) return undefined
  return normalized
}

const resolveSuggestionImage = (record: Record<string, unknown>) => {
  const direct = toSafeText(
    record.productImageUrl || record.imageUrl || record.coverImageUrl || record.featuredImage,
  )
  if (direct) return direct
  const media = Array.isArray(record.media) ? record.media : []
  for (const entry of media) {
    if (!entry || typeof entry !== 'object') continue
    const mediaUrl = toSafeText((entry as Record<string, unknown>).url)
    if (mediaUrl) return mediaUrl
  }
  return ''
}

export const rankProducts = (products: unknown[], searchTerm: string): SearchSuggestion[] => {
  const normalizedTerm = searchTerm.trim().toLowerCase()
  if (!normalizedTerm) return []

  const buildSuggestion = ({
    slug,
    publicCode,
    name,
    imageUrl,
    score,
  }: {
    slug: string
    publicCode?: string
    name: string
    imageUrl: string
    score: number
  }): SearchSuggestion => ({
    slug,
    ...(publicCode ? { publicCode } : {}),
    name,
    imageUrl,
    score,
  })

  return products
    .map((entry): SearchSuggestion | null => {
      if (!entry || typeof entry !== 'object') return null
      const record = entry as Record<string, unknown>
      const slug = toSafeText(record.slug)
      const publicCode = normalizePublicCode(record.publicCode)
      const name = toSafeText(record.name)
      if (!slug) return null
      const normalizedName = name.toLowerCase()
      const normalizedSlug = slug.toLowerCase()
      if (normalizedSlug === normalizedTerm) {
        return buildSuggestion({
          slug,
          publicCode,
          name: name || slug,
          imageUrl: resolveSuggestionImage(record),
          score: 0,
        })
      }
      if (normalizedName === normalizedTerm) {
        return buildSuggestion({
          slug,
          publicCode,
          name: name || slug,
          imageUrl: resolveSuggestionImage(record),
          score: 1,
        })
      }
      if (normalizedName.startsWith(normalizedTerm)) {
        return buildSuggestion({
          slug,
          publicCode,
          name: name || slug,
          imageUrl: resolveSuggestionImage(record),
          score: 2,
        })
      }
      if (normalizedSlug.startsWith(normalizedTerm)) {
        return buildSuggestion({
          slug,
          publicCode,
          name: name || slug,
          imageUrl: resolveSuggestionImage(record),
          score: 3,
        })
      }
      if (normalizedName.includes(normalizedTerm) || normalizedSlug.includes(normalizedTerm)) {
        return buildSuggestion({
          slug,
          publicCode,
          name: name || slug,
          imageUrl: resolveSuggestionImage(record),
          score: 4,
        })
      }
      return null
    })
    .filter((entry): entry is SearchSuggestion => Boolean(entry))
    .sort(
      (left, right) =>
        left.score - right.score ||
        left.name.localeCompare(right.name, 'it', { sensitivity: 'base' }),
    )
}
